import { ILogger } from "../Logging/Logger";
import { BoundingBox } from '@spi/Common/Util';
import { ConvexShape } from "./ConvexShape";
import { HeightField } from "./HeightField";
import { HeightSpan, NeighbourDirection } from "./HeightSpan";
import { NavigationMeshSettings, NavigationMesh } from "./NavigationMesh";
import { PathNode } from "./PathNode";

export class NavigationMeshSegment {

    private convexShapes: ConvexShape[];

    public edges: Map<HeightSpan, ConvexShape>;
    public heightField: HeightField;
    public usedSpans: Map<HeightSpan, ConvexShape>;
    public pathNodes: PathNode[];
    public processed: boolean;

    public neighboursVisited: Map<NavigationMeshSegment, boolean>;

    constructor(
        public parent: NavigationMesh,
        public settings: NavigationMeshSettings,
        public logger: ILogger,
        public bounds: BoundingBox,
        public index: number[]) {

        this.neighboursVisited = new Map<NavigationMeshSegment, boolean>();
        this.heightField = new HeightField(settings, this.bounds);
        this.processed = false;
    }

    public addNeighbour(neighbour: NavigationMeshSegment) {
        this.neighboursVisited.set(neighbour, false);
    }

    public neighbourVisited(neighbour: NavigationMeshSegment) {
        this.neighboursVisited.set(neighbour, true);

        if ([...this.neighboursVisited.values()].every(visited => visited)) {
            this.edges = null;
        }
    }

    public done() {

        this.edges = this.determineEdges();
        this.heightField = null;
        this.usedSpans = null;
        this.convexShapes = null;

        this.processed = true;
    }

    /**
     * Makes a mapping of all the edge spans and their associated shapes.
     */
    private determineEdges(): Map<HeightSpan, ConvexShape> {

        let result = new Map<HeightSpan, ConvexShape>();
        this.usedSpans.forEach((shape, span) => {
            if (this.isEdgeSpan(span)) {
                // it's an edge
                result.set(span, shape);
            }
        });

        return result;
    }

    public getEdgeSpans(spans: HeightSpan[]): HeightSpan[] {
        let result: HeightSpan[] = [];
        spans.forEach(span => {
            if (this.isEdgeSpan(span)) {
                // it's an edge
                result.push(span);
            }
        });

        return result;
    }

    public getNoneEdgeSpans(spans: HeightSpan[]): HeightSpan[] {
        let result: HeightSpan[] = [];
        spans.forEach(span => {
            if (!this.isEdgeSpan(span)) {
                // it's not an edge
                result.push(span);
            }
        });

        return result;
    }

    /**
     * Checks whether the given span is at the edge of the segment bounds.
     * @param span the span to check
     */
    private isEdgeSpan(span: HeightSpan): boolean {
        return span.spanLocation[0] * this.settings.voxelSize == this.bounds.min[0]
            || span.spanLocation[0] * this.settings.voxelSize == this.bounds.max[0] - this.settings.voxelSize
            || span.spanLocation[1] * this.settings.voxelSize == this.bounds.min[1]
            || span.spanLocation[1] * this.settings.voxelSize == this.bounds.max[1] - this.settings.voxelSize;
    }

    public generatePathNodes() {
        this.pathNodes = [];

        let nodeMap = new Map<ConvexShape, PathNode>();
        for (let shape of this.convexShapes) {
            let bounds = shape.getBoundingBox();
            let node = new PathNode(bounds.min, bounds.max);
            node.heightGrid = shape.createHeightGrid();
            node.reference = shape.groupReference;
            nodeMap.set(shape, node);

            shape.node = node;

            this.pathNodes.push(node);
        }

        // Link the neighbour nodes
        for (let shape of this.convexShapes) {
            let primaryNode = nodeMap.get(shape);

            for (let n of shape.neighbourSet) {
                let neighbour = n.node;
                primaryNode.addNeighbour(neighbour);
            }
        }
    }

    public generateShapes(spans: HeightSpan[]) {
        let shapeList: ConvexShape[] = [];
        // Loop over all spans
        for (let span of spans) {

            // And attempt to expand them into larger (convex) shapes
            let shape = this.expand(span);
            if (shape) {
                // When a new shape is made, add it to the list.
                shapeList.push(shape);
            }
        }
        this.convexShapes = shapeList;
    }

    /**
     * Link all shapes to each other so their links can be traversed.
     */
    public findShapeNeighbours() {
        // Loop through all spans and the shapes they belong to
        this.usedSpans.forEach((shape, span) => {
            // And over all of the neighbours of the span
            for (let direction in span.compassNeighbours) {
                let neighbourSpan = span.compassNeighbours[direction];

                if (this.usedSpans.has(neighbourSpan)) {
                    // Get the shape associated with the neighbour span
                    let neighbourShape = this.usedSpans.get(neighbourSpan);

                    if (neighbourShape != shape) {
                        // When the shape is different than the shape of the origin span
                        // we have found a new neighbour of that shape.
                        shape.addNeighbour(neighbourShape);
                    }
                }
            }
        });
    }

    /**
     * Attempts to expand the span into a convex shape that is as large
     * as possible (within bounds). When a span is already associated 
     * with a shape then we return null.
     * @param span the span to expand into a shape
     */
    private expand(span: HeightSpan): ConvexShape {

        // When the span has already been associated with a shape return null
        if (this.usedSpans.has(span)) {
            return null;
        }

        // When the span is not traversable return null
        if (!span.traversable) {
            return null;
        }

        // Span is valid and not used, start by creating a new shape object.
        let shape = new ConvexShape(this.settings.voxelSize);
        shape.addSpan(span);
        shape.groupReference = span.groupReference;

        // Mark the span as used and associate it with the new shape object.
        this.usedSpans.set(span, shape);

        // Keep track of the expansion in all directions.
        let expansion: { [key in NeighbourDirection]: number } = {
            n: 0,
            s: 0,
            w: 0,
            e: 0
        };

        let finalExpansion = new Map<string, number>();

        // A check to see if the neighbour span may be connected. This is only
        // allowed when the next span is not yet associated with a shape and 
        // the span has the same reference.
        let predicate = (s: HeightSpan, prev: HeightSpan) => {
            let boundShape = this.usedSpans.get(s);
            return (boundShape ? boundShape == shape : true)
                && s.groupReference == prev.groupReference; // grouped spans may only group with identical group spans
        };

        let expanded = false;
        do {
            expanded = false;

            // Attempt to expand in each direction
            for (let direction in span.compassNeighbours) {

                // When the maximum expansion in a direction is reached we stop.
                if (finalExpansion.has(direction)) {
                    continue;
                }

                // Fetch the spans neighbours in a particular direction.
                let nextNeighbour = this.getSpanNeighbours(span, direction, expansion[direction] + 1, predicate);

                // Assuming a neigbour was found
                if (nextNeighbour.exists) {
                    let secondaryResults: { [key: string]: HeightSpan[] } = {};

                    let canExpand = true;
                    // Get the other directions that will be affected by the expansion
                    for (let affectedDirection of this.getAffectedDirections(direction)) {

                        if (expansion[affectedDirection] == 0) {
                            continue;
                        }

                        // Fetch the list of neighbours and store it.
                        let aNeighbour = this.getSpanNeighbours(nextNeighbour.lastNeighbour, affectedDirection, expansion[affectedDirection], predicate);

                        if (aNeighbour.exists) {
                            secondaryResults[affectedDirection] = aNeighbour.neighbourList;
                        } else {
                            canExpand = false;
                        }
                    }

                    // We reached the maximum expansion in this direction, abort.
                    if (expansion[direction] >= this.settings.maxConvexExpansion) {
                        canExpand = false;
                    }

                    if (canExpand) {

                        // We have expanded, ensure that the loop continues to expand.
                        expanded = true;

                        expansion[direction]++;

                        // Create the list of spans added to the shape
                        let spans = [nextNeighbour.lastNeighbour];

                        for (let key in secondaryResults) {
                            spans.push(...secondaryResults[key]);
                        }

                        // Add the spans to the shape and mark the shapes as used.
                        spans.forEach(s => {
                            shape.addSpan(s);
                            this.usedSpans.set(s, shape);
                        });
                    } else {
                        // Could not expand, record the final expansion.
                        finalExpansion.set(direction, expansion[direction]);
                    }
                }

            }
        }
        while (expanded);

        return shape;
    }

    private getSpanNeighbours(span: HeightSpan, direction: string, distance: number, predicate: (s: HeightSpan, prev: HeightSpan) => boolean): NeighbourData {

        let data: NeighbourData = {
            lastNeighbour: null,
            neighbourList: [],
            exists: false
        };

        let currentSpan = span;
        let found = true;
        for (let i = 0; i < distance; i++) {
            if (direction in currentSpan.compassNeighbours) {
                let s = currentSpan;
                currentSpan = currentSpan.compassNeighbours[direction];
                data.lastNeighbour = currentSpan;
                if (predicate(currentSpan, s)) {
                    data.neighbourList.push(currentSpan);
                } else {
                    found = false;
                    break; // predicate failed
                }

            } else {
                found = false;
                break; // has no neighbour in that direction
            }
        }

        data.exists = found;

        return data;
    }

    private getAffectedDirections(direction: string): NeighbourDirection[] {
        switch (direction) {
            case NeighbourDirection.North:
            case NeighbourDirection.South:
                return [NeighbourDirection.West, NeighbourDirection.East];
            case NeighbourDirection.East:
            case NeighbourDirection.West:
                return [NeighbourDirection.North, NeighbourDirection.South];
        }

        throw new Error(`Invalid direction ${direction}`);
    }
}

type NeighbourData = {
    lastNeighbour: HeightSpan,
    neighbourList: HeightSpan[],
    exists: boolean
}